import { useState, useEffect, useRef } from 'react';
import InputField from '@/components/common/InputField';
import { Column, ColumnOption } from '@/types/columns';
import { toastMessage } from '@/utils/toast-message';
import { useUser } from '@/contexts/UserContext';

// Define types locally since they're not exported from a central location
interface LeadData {
    uuid?: string;
    lead_id?: number;
    name?: string;
    email?: string;
    phone?: string;
    message?: string;
    created_date?: string;
    last_updated_date?: string;
    status?: string;
    status_uuid?: string;
    source?: string;
    source_uuid?: string;
    grade?: number;
    grade_uuid?: string;
    statusOption?: { uuid: string; opt_name: string };
    sourceOption?: { uuid: string; opt_name: string };
    gradeOption?: { uuid: string; opt_name: string };
    formattedCustomInfo?: Record<string, any>;
    customFields?: Array<any>;
    customInfo?: Array<any>;
    assignments?: Array<{
        is_active: boolean;
        user_uuid?: string;
        user?: { uuid: string };
    }>;
}

type Option = ColumnOption;
import Loader from '@/components/common/Loader/Loader';
import { fetchLeadColumns, getLeadDetails, createLead, updateLead } from '@/actions/lead-manager';
import ColumnsService from '@/utils/columnsCache';
import Button from '@/components/common/Button';

interface FormValues {
    [key: string]: string | number | null; // Key will be column UUID instead of col_name
}

// Store column information for lookup by UUID
interface ColumnLookup {
    [uuid: string]: Column;
}

interface DynamicLeadFormProps {
    onSubmitSuccess?: () => void;
    mode?: 'create' | 'update';
    leadData?: LeadData;
    leadUuid?: string | null;
    onClose?: () => void; // Add onClose prop for modal closure
    refreshData?: () => void;
    activeUsersForDropdown?: UserOption[]; // Users passed from parent to avoid API call
    hideAssignedTo?: boolean; // Hide the Assigned To section (for admin view)
    isAdminView?: boolean; // Flag to indicate if editing from admin view (for remark type)
    readOnly?: boolean; // View only: every field disabled, no remarks box or Update button
    readOnlyNote?: string; // Shown at the top when readOnly (e.g. where to edit instead)
    labelOverrides?: Record<string, string>; // col_name -> label shown instead of the column's own label
}

interface UserOption {
    value: string;
    label: string;
    email?: string;
    role?: string;
}

const DynamicLeadForm = ({
    onSubmitSuccess,
    mode = 'create',
    leadData,
    leadUuid,
    onClose,
    refreshData,
    activeUsersForDropdown = [],
    hideAssignedTo = false,
    readOnly = false,
    readOnlyNote,
    labelOverrides,
    isAdminView = false
}: DynamicLeadFormProps) => {
    const [columns, setColumns] = useState<Column[]>([]);
    const [loading, setLoading] = useState(false);
    const [formValues, setFormValues] = useState<FormValues>({});
    const [fieldErrors, setFieldErrors] = useState<{ [key: string]: string }>({});
    const [leadDetails, setLeadDetails] = useState<LeadData | null>(leadData || null);

    const [remarks, setRemarks] = useState<string>('');
    const [remarksError, setRemarksError] = useState<string>('');
    const [activeUsers, setActiveUsers] = useState<UserOption[]>([]);
    const [selectedOwner, setSelectedOwner] = useState<string>('');

    // Track original status for is_client=1 validation
    const [originalStatusUuid, setOriginalStatusUuid] = useState<string | null>(null);

    // Get is_client from user context
    const { userData } = useUser();
    const isClientSchool = userData?.data?.data?.client?.is_client === 1;

    // Refs for scroll functionality
    const remarksFieldRef = useRef<HTMLDivElement>(null);
    const formContainerRef = useRef<HTMLDivElement>(null);

    // Fetch columns and lead data when component mounts
    useEffect(() => {
        fetchFormColumns();
        if (mode === 'update' && activeUsersForDropdown.length > 0) {
            // Use the prop instead of fetching
            setActiveUsers(activeUsersForDropdown);
        }
    }, [leadUuid, mode, activeUsersForDropdown]);

    // Map lead data to form values
    useEffect(() => {
        if (leadDetails && columns.length > 0) {
            mapLeadDataToFormValues();
        }
    }, [leadDetails, columns]);

    // Set the selected owner when activeUsers is loaded (separate useEffect to avoid race condition)
    useEffect(() => {
        console.log('🔍 Owner Selection Debug:', {
            hasLeadDetails: !!leadDetails,
            hasAssignments: leadDetails?.assignments?.length || 0,
            activeUsersCount: activeUsers.length,
            assignments: leadDetails?.assignments,
            activeUsers: activeUsers
        });

        if (leadDetails && leadDetails.assignments && leadDetails.assignments.length > 0 && activeUsers.length > 0) {
            // Find the active assignment
            const activeAssignment = leadDetails.assignments.find(a => a.is_active) || leadDetails.assignments[0];
            console.log('📌 Active Assignment:', activeAssignment);

            if (activeAssignment) {
                // Use user_uuid directly if available, otherwise check nested user object
                const ownerUuid = activeAssignment.user_uuid || activeAssignment.user?.uuid;
                console.log('🎯 Owner UUID:', ownerUuid);

                if (ownerUuid) {
                    // Verify the UUID exists in activeUsers
                    const userExists = activeUsers.some(u => u.value === ownerUuid);
                    console.log('✅ User exists in dropdown?', userExists);
                    console.log('🔑 Available UUIDs:', activeUsers.map(u => u.value));

                    if (userExists) {
                        console.log('✓ Setting selectedOwner to:', ownerUuid);
                        setSelectedOwner(ownerUuid);
                    } else {
                        console.warn('❌ Owner UUID not found in activeUsers list:', ownerUuid);
                    }
                }
            }
        }
    }, [leadDetails, activeUsers]);

    const handleRemarksChange = (value: string) => {
        setRemarks(value);
        if (remarksError) {
            setRemarksError('');
        }
    };

    // REMOVED: fetchActiveUsers - now using prop passed from parent (Leads page)
    // This eliminates redundant API calls on every lead edit
    // const fetchActiveUsers = async () => { ... }

    const fetchFormColumns = async () => {
        try {
            setLoading(true);
            // Try to get columns from cache first, then fallback to fetchLeadColumns
            // Note: No need to force refresh in update mode - 30min cache is sufficient
            let response = await ColumnsService.getColumns(true);
            
            // If cache service fails, fallback to original method
            if (!response.success) {
                console.log('Cache failed, falling back to fetchLeadColumns');
                response = await fetchLeadColumns();
            }

            if (response.success && response.columns) {
                const activeColumns = response.columns;
                setColumns(activeColumns);

                // Create a lookup object indexed by column UUID
                const lookup: ColumnLookup = {};
                activeColumns.forEach(column => {
                    lookup[column.uuid] = column;
                });

                // Initialize form values with empty strings or default values using UUID as keys
                const initialValues: FormValues = {};
                activeColumns.forEach(column => {
                    if (column.col_type === 'select' && column.options.length > 0) {
                        // For select fields, use the "please_select" value as default if exists
                        const defaultOption = column.options.find(opt => opt.opt_value === 'please_select');
                        initialValues[column.uuid] = defaultOption ? defaultOption.opt_value : '';
                    } else {
                        initialValues[column.uuid] = '';
                    }
                });

                setFormValues(initialValues);

                // If we're in update mode
                if (mode === 'update') {
                    if (leadData) {
                        // Use provided data immediately for instant display
                        setLeadDetails(leadData);

                        // Only re-fetch if leadData is missing critical information (not just assignments)
                        // Removed the assignments check to prevent unnecessary API calls
                        // The parent component should provide complete leadData when opening the form
                    } else if (leadUuid) {
                        // No data provided, fetch fresh
                        fetchLeadDetailsById(leadUuid);
                    }
                } else if (leadData && mode === 'create') {
                    // For create mode, use provided data if available
                    setLeadDetails(leadData);
                }
            } else {
                setColumns([]);
            }
        } catch (error) {
            console.error('Error fetching columns:', error);
            setColumns([]);
        } finally {
            setLoading(false);
        }
    };

    // Fetch lead details for update mode
    const fetchLeadDetailsById = async (uuid: string) => {
        try {
            setLoading(true);
            const response = await getLeadDetails(uuid);

            if (response.success && response.data) {
                setLeadDetails(response.data);
            }
        } catch (error) {
            console.error('Error fetching lead details:', error);
        } finally {
            setLoading(false);
        }
    };

    // Map lead data to form values for update mode
    const mapLeadDataToFormValues = () => {
        if (!leadDetails) {
            return;
        }

        // Start with an empty object for the mapped values
        const mappedValues: FormValues = {};

        // Track which column UUIDs have been mapped from primary fields
        // Primary fields: name, email, phone, message, source, status, created_date, last_updated_date, lead_id
        const primaryFieldColumnUuids = new Set<string>();

        // Find columns for basic fields by their col_name
        const nameColumn = columns.find(col => col.col_name === 'name');
        const emailColumn = columns.find(col => col.col_name === 'email');
        const phoneColumn = columns.find(col => col.col_name === 'phone');
        const messageColumn = columns.find(col => col.col_name === 'message');

        // Map basic fields if they exist
        if (leadDetails.name !== undefined && nameColumn) {
            mappedValues[nameColumn.uuid] = leadDetails.name;
            primaryFieldColumnUuids.add(nameColumn.uuid);
        }

        if (leadDetails.email !== undefined && emailColumn) {
            mappedValues[emailColumn.uuid] = leadDetails.email;
            primaryFieldColumnUuids.add(emailColumn.uuid);
        }

        if (leadDetails.phone !== undefined && phoneColumn) {
            mappedValues[phoneColumn.uuid] = leadDetails.phone;
            primaryFieldColumnUuids.add(phoneColumn.uuid);
        }

        if (leadDetails.message !== undefined && messageColumn) {
            mappedValues[messageColumn.uuid] = leadDetails.message;
            primaryFieldColumnUuids.add(messageColumn.uuid);
        }

        // Map created_date if it exists
        if (leadDetails.created_date !== undefined) {
            // Find the date column
            const dateColumn = columns.find(col => col.col_type === 'dateTime' && (col.col_name === 'created_date' || col.col_name === 'createdDate'));
            if (dateColumn) {
                // Format the date as required for datetime-local input (YYYY-MM-DDTHH:MM)
                try {
                    const dateStr = leadDetails.created_date;
                    // Parse the date string and convert to format needed for datetime-local input
                    const [datePart, timePart] = dateStr.split(' ');
                    const formattedDate = `${datePart}T${timePart.substring(0, 5)}`; // Remove seconds
                    mappedValues[dateColumn.uuid] = formattedDate;
                    primaryFieldColumnUuids.add(dateColumn.uuid);
                } catch (err) {
                    console.error('Error formatting date:', err);
                }
            }
        }

        // Map last_updated_date if it exists
        if (leadDetails.last_updated_date !== undefined) {
            const updatedDateColumn = columns.find(col => col.col_type === 'dateTime' && (col.col_name === 'last_updated_date' || col.col_name === 'updated_date' || col.col_name === 'lastUpdatedDate'));
            if (updatedDateColumn) {
                try {
                    const dateStr = leadDetails.last_updated_date;
                    const [datePart, timePart] = dateStr.split(' ');
                    const formattedDate = `${datePart}T${timePart.substring(0, 5)}`;
                    mappedValues[updatedDateColumn.uuid] = formattedDate;
                    primaryFieldColumnUuids.add(updatedDateColumn.uuid);
                } catch (err) {
                    console.error('Error formatting updated date:', err);
                }
            }
        }

        // Map lead_id if it exists
        if (leadDetails.lead_id !== undefined) {
            const leadIdColumn = columns.find(col => col.col_name === 'lead_id' || col.col_name === 'leadId');
            if (leadIdColumn) {
                mappedValues[leadIdColumn.uuid] = leadDetails.lead_id;
                primaryFieldColumnUuids.add(leadIdColumn.uuid);
            }
        }

        // Map status field if it exists
        const statusColumn = columns.find(col => col.col_name === 'status');
        if (statusColumn) {
            // Try different status field formats
            let statusValue = null;
            if (leadDetails.status_uuid) {
                statusValue = leadDetails.status_uuid;
            } else if (leadDetails.statusOption?.uuid) {
                statusValue = leadDetails.statusOption.uuid;
            } else if (leadDetails.status) {
                statusValue = leadDetails.status;
            }

            if (statusValue) {
                mappedValues[statusColumn.uuid] = statusValue;
                // Save original status for is_client=1 validation (only in update mode)
                if (mode === 'update' && !originalStatusUuid) {
                    setOriginalStatusUuid(statusValue);
                }
            }
            primaryFieldColumnUuids.add(statusColumn.uuid);
        }

        // Try multiple possible source field mappings
        const sourceColumn = columns.find(col => col.col_name === 'source');
        if (sourceColumn) {
            // Try different source field formats
            if (leadDetails.source_uuid) {
                mappedValues[sourceColumn.uuid] = leadDetails.source_uuid;
            } else if (leadDetails.sourceOption?.uuid) {
                mappedValues[sourceColumn.uuid] = leadDetails.sourceOption.uuid;
            } else if (leadDetails.source) {
                mappedValues[sourceColumn.uuid] = leadDetails.source;
            }
            primaryFieldColumnUuids.add(sourceColumn.uuid);
        }

        // Map grade field if it exists (primary field in crm_leads)
        const gradeColumn = columns.find(col => col.col_name === 'grade');
        if (gradeColumn) {
            // Try different grade field formats
            if (leadDetails.grade_uuid) {
                mappedValues[gradeColumn.uuid] = leadDetails.grade_uuid;
            } else if (leadDetails.gradeOption?.uuid) {
                mappedValues[gradeColumn.uuid] = leadDetails.gradeOption.uuid;
            } else if (leadDetails.grade) {
                mappedValues[gradeColumn.uuid] = leadDetails.grade;
            }
            primaryFieldColumnUuids.add(gradeColumn.uuid);
        }

        // Map custom fields from formattedCustomInfo if it exists
        if (leadDetails.formattedCustomInfo) {
            Object.entries(leadDetails.formattedCustomInfo).forEach(([fieldName, fieldData]: [string, any]) => {
                // Find the column by name
                const column = columns.find(col => col.col_name === fieldName);
                if (column && fieldData.col_uuid) {
                    // Use col_uuid from the fieldData to find the correct column, then set the value
                    const columnByUuid = columns.find(col => col.uuid === fieldData.col_uuid);
                    // Skip if this column was already mapped from a primary field
                    if (columnByUuid && !primaryFieldColumnUuids.has(columnByUuid.uuid)) {
                        if (columnByUuid.col_type === 'select') {
                            // For select fields, use valueUuid if available, otherwise fall back to value matching
                            if (fieldData.valueUuid) {
                                mappedValues[columnByUuid.uuid] = fieldData.valueUuid;
                            } else {
                                // Fallback: find the option UUID that matches the value
                                const matchingOption = columnByUuid.options?.find(opt => opt.opt_value === fieldData.value || opt.opt_name === fieldData.value);
                                if (matchingOption) {
                                    mappedValues[columnByUuid.uuid] = matchingOption.uuid;
                                } else {
                                    // If no matching option found, use the value as is
                                    mappedValues[columnByUuid.uuid] = fieldData.value;
                                }
                            }
                        } else if (columnByUuid.col_type === 'dateTime' || columnByUuid.col_type === 'datetime') {
                            // For dateTime fields, format the value for datetime-local input
                            if (fieldData.value) {
                                try {
                                    const date = new Date(fieldData.value);
                                    if (!isNaN(date.getTime())) {
                                        // Format to YYYY-MM-DDTHH:MM for datetime-local input
                                        const year = date.getFullYear();
                                        const month = String(date.getMonth() + 1).padStart(2, '0');
                                        const day = String(date.getDate()).padStart(2, '0');
                                        const hours = String(date.getHours()).padStart(2, '0');
                                        const minutes = String(date.getMinutes()).padStart(2, '0');
                                        mappedValues[columnByUuid.uuid] = `${year}-${month}-${day}T${hours}:${minutes}`;
                                    } else {
                                        mappedValues[columnByUuid.uuid] = fieldData.value;
                                    }
                                } catch (err) {
                                    console.error('Error parsing datetime from formattedCustomInfo:', err);
                                    mappedValues[columnByUuid.uuid] = fieldData.value;
                                }
                            }
                        } else {
                            mappedValues[columnByUuid.uuid] = fieldData.value;
                        }
                    }
                }
            });
        }

        // Map from customFields array if it exists and has items
        if (leadDetails.customFields && leadDetails.customFields.length > 0) {
            leadDetails.customFields.forEach((customField: any) => {
                if (customField.column?.uuid) {
                    const column = columns.find(col => col.uuid === customField.column.uuid);

                    // Skip if this column was already mapped from a primary field
                    if (column && !primaryFieldColumnUuids.has(column.uuid)) {
                        // Determine the appropriate value based on field type
                        let fieldValue = null;

                        if (customField.column.col_type === 'select') {
                            // For select fields, check multiple sources for the option UUID
                            // Priority: optionDetails.uuid > value_text > value_int (find by opt_id)
                            if (customField.optionDetails?.uuid) {
                                // NEW: Check optionDetails first (current API format)
                                fieldValue = customField.optionDetails.uuid;
                            } else if (customField.value_text) {
                                // value_text should contain the option UUID
                                fieldValue = customField.value_text;
                            } else if (customField.value_int) {
                                // Fallback: find option by opt_id if UUID is not available
                                const matchingOption = column.options?.find(opt =>
                                    opt.opt_id === customField.value_int
                                );
                                fieldValue = matchingOption ? matchingOption.uuid : null;
                            }
                        } else if (customField.column.col_type === 'text' || customField.column.col_type === 'textarea') {
                            fieldValue = customField.value_text;
                        } else if (customField.column.col_type === 'number' || customField.column.col_type === 'numeric') {
                            fieldValue = customField.value_int || customField.value_decimal;
                        } else if (customField.column.col_type === 'date') {
                            fieldValue = customField.value_date;
                        } else if (customField.column.col_type === 'dateTime' || customField.column.col_type === 'datetime') {
                            // Try value_datetime first, fall back to value_text
                            if (customField.value_datetime) {
                                fieldValue = customField.value_datetime;
                            } else if (customField.value_text) {
                                // Parse the date string and convert to datetime-local format (YYYY-MM-DDTHH:MM)
                                try {
                                    const date = new Date(customField.value_text);
                                    if (!isNaN(date.getTime())) {
                                        // Format to YYYY-MM-DDTHH:MM for datetime-local input
                                        const year = date.getFullYear();
                                        const month = String(date.getMonth() + 1).padStart(2, '0');
                                        const day = String(date.getDate()).padStart(2, '0');
                                        const hours = String(date.getHours()).padStart(2, '0');
                                        const minutes = String(date.getMinutes()).padStart(2, '0');
                                        fieldValue = `${year}-${month}-${day}T${hours}:${minutes}`;
                                    } else {
                                        fieldValue = customField.value_text;
                                    }
                                } catch (err) {
                                    console.error('Error parsing datetime from value_text:', err);
                                    fieldValue = customField.value_text;
                                }
                            }
                        } else {
                            // Default to text value
                            fieldValue = customField.value_text;
                        }
                        
                        if (fieldValue !== null && fieldValue !== undefined) {
                            mappedValues[customField.column.uuid] = fieldValue;
                        }
                    }
                }
            });
        }

        // Map from customInfo array if it exists and has items (fallback)
        if (leadDetails.customInfo && leadDetails.customInfo.length > 0) {
            leadDetails.customInfo.forEach((item: any) => {
                // Skip if this column was already mapped from a primary field
                if (item.column?.uuid && !primaryFieldColumnUuids.has(item.column.uuid)) {
                    if (item.column.col_type === 'select' && item.optionDetailsbyUuid) {
                        mappedValues[item.column.uuid] = item.optionDetailsbyUuid.uuid;
                    } else {
                        mappedValues[item.column.uuid] = item.custom_field_value;
                    }
                }
            });
        }

        setFormValues(prev => ({ ...prev, ...mappedValues }));
    };

    const handleChange = (uuid: string, value: string | number) => {
        setFormValues(prev => ({
            ...prev,
            [uuid]: value
        }));

        // Clear error for this field if it exists
        if (fieldErrors[uuid]) {
            setFieldErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[uuid];
                return newErrors;
            });
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (readOnly) return;

        // Validate required fields
        const requiredFields = columns.filter(col => col.col_required);
        const missingFields = requiredFields.filter(field => {
            const value = formValues[field.uuid];
            return value === null || value === undefined || value === '';
        });

        if (missingFields.length > 0) {
            // Set individual field errors
            const newErrors: { [key: string]: string } = {};
            missingFields.forEach(field => {
                newErrors[field.uuid] = `${field.col_label} is required`;
            });
            setFieldErrors(newErrors);
            toastMessage(`Please fill in required fields: ${missingFields.map(f => f.col_label).join(', ')}`, 'error')
            return;
        }

        // Validate remarks for edit mode
        if (mode === 'update' && (!remarks || remarks.trim() === '')) {
            setRemarksError('Please provide a reason for editing this lead');
            toastMessage('Please provide a reason for editing this lead', 'error');

            // Scroll to remarks field
            if (remarksFieldRef.current) {
                remarksFieldRef.current.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center'
                });

                // Also focus on the textarea for better UX
                const textarea = remarksFieldRef.current.querySelector('textarea');
                if (textarea) {
                    setTimeout(() => {
                        textarea.focus();
                    }, 300);
                }
            }

            return;
        }

        // Validate status change for is_client=1 schools
        if (mode === 'update' && isClientSchool) {
            const statusColumn = columns.find(col => col.col_name === 'status');
            if (statusColumn) {
                const currentStatusUuid = formValues[statusColumn.uuid];
                if (originalStatusUuid && currentStatusUuid === originalStatusUuid) {
                    toastMessage('Status change is mandatory. Please update the lead status.', 'error');

                    // Set field error for status column
                    setFieldErrors(prev => ({
                        ...prev,
                        [statusColumn.uuid]: 'Status change is mandatory'
                    }));

                    return;
                }
            }
        }

        try {
            setLoading(true);
            let response;

            if (mode === 'create') {
                // For create mode, find standard fields and map them by name
                const formData: { [key: string]: any } = {};

                // Find standard fields by their col_name and map them accordingly
                columns.forEach(column => {
                    const value = formValues[column.uuid];
                    formData[column.uuid] = value;
                });

                const createData = {
                    formData: formData
                };

                response = await createLead(createData);

                if (response.success) {
                    // Use the response message if available, otherwise use a default message
                    const successMessage = response.message || (mode === 'create' ? 'Lead created successfully!' : 'Lead updated successfully!');
                    toastMessage(successMessage, 'success');

                    // Close the modal if onClose prop exists
                    if (onClose) {
                        onClose();
                    }

                    // Refresh the data if refreshData prop exists
                    if (refreshData) {
                        await refreshData();
                    }

                    // Call onSubmitSuccess callback if provided
                    if (onSubmitSuccess) {
                        onSubmitSuccess();
                    }
                } else {
                    // Show error message from API response
                    const errorMessage = response.message || 'Failed to create lead';
                    toastMessage(errorMessage, 'error');
                }

            } else {
                // Update existing lead
                if (!leadDetails || !leadDetails.uuid) {
                    toastMessage('Lead UUID is missing for update', 'warning');
                    setLoading(false);
                    return;
                }

                // For update, prepare only the enabled fields
                const updateFormData: { [key: string]: any } = {};

                // Identify disabled fields in update mode
                const disabledFields = ['phone', 'created_date', 'createdDate', 'message'];

                // Add only enabled fields to the updateData
                columns.forEach(column => {
                    // Skip disabled fields in update mode
                    if (!disabledFields.includes(column.col_name)) {
                        updateFormData[column.uuid] = formValues[column.uuid];
                    }
                });

                updateFormData['remarks'] = remarks;

                response = await updateLead(leadDetails.uuid, updateFormData, remarks.trim(), selectedOwner, false, isAdminView);

                if (response.success) {
                    toastMessage('Lead updated successfully!', 'success');
                    
                    // Reset remarks after successful update
                    setRemarks('');
                    setRemarksError('');

                    // Close the modal if onClose prop exists
                    if (onClose) {
                        onClose();
                    }
                    if (refreshData) {
                        refreshData();
                    }
                } else {
                    toastMessage('Failed to update lead', 'error');
                }
            }
        } catch (error) {
            console.error('Error submitting form:', error);
            // Extract error message from API response or use generic message
            const errorMessage = error instanceof Error ? error.message : 'An error occurred while processing your request';
            toastMessage(errorMessage, 'error');
        } finally {
            setLoading(false);
        }
    };

    // Map column.options to InputField options format
    const mapOptions = (options: Option[]) => {
        return options
            .sort((a, b) => a.opt_order - b.opt_order)
            .map(option => ({
                value: option.uuid, // Use UUID for value to match the API response
                label: option.opt_name
            }));
    };

    // Render different field types using InputField component
    const renderField = (column: Column) => {
        const { uuid, col_name, col_label, col_type, col_required, col_readonly, col_description, options } = column;

        // Type-safe value access - ensure we convert to string if needed for React props
        const fieldValue = formValues[uuid] !== null && formValues[uuid] !== undefined
            ? String(formValues[uuid])
            : '';

        // Disable phone, created_date, and message in edit mode (source is now editable)
        const shouldDisableInEditMode = mode === 'update' &&
            (col_name === 'phone' ||
                col_name === 'created_date' ||
                col_name === 'createdDate' ||
                col_name === 'message'
            );

        // Common props for all field types
        const commonProps = {
            id: uuid,
            name: uuid,
            // Remove label since we're showing it separately in the grid layout
            required: col_required,
            disabled: loading || col_readonly || shouldDisableInEditMode || readOnly,
            placeholder: readOnly ? '' : col_description || `Enter ${labelOverrides?.[col_name] || col_label}`,
            value: fieldValue,
            onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
                handleChange(uuid, e.target.value);
            },
            error: !!fieldErrors[uuid],
            className: 'w-full'
        };

        // For select type, add options
        if (col_type === 'select') {
            return (
                <InputField
                    {...commonProps}
                    type="select"
                    options={mapOptions(options)}
                />
            );
        }

        // For textarea type
        if (col_type === 'textarea') {
            return (
                <InputField
                    {...commonProps}
                    type="textarea"
                    rows={1}
                />
            );
        }

        // For dateTime type
        if (col_type === 'dateTime') {
            return (
                <InputField
                    {...commonProps}
                    type="datetime-local"
                />
            );
        }

        // For date type
        if (col_type === 'date') {
            return (
                <InputField
                    {...commonProps}
                    type="date"
                />
            );
        }

        // For email type
        if (col_type === 'email') {
            return (
                <InputField
                    {...commonProps}
                    type="email"
                    placeholder="example@email.com"
                />
            );
        }

        // For number type
        if (col_type === 'number' || col_type === 'numeric') {
            return (
                <InputField
                    {...commonProps}
                    type="number"
                />
            );
        }

        // For phone type
        if (col_type === 'phone' || col_type === 'tel') {
            return (
                <InputField
                    {...commonProps}
                    type="tel"
                    placeholder="+1 (555) 123-4567"
                />
            );
        }

        // For URL type
        if (col_type === 'url') {
            return (
                <InputField
                    {...commonProps}
                    type="text"
                    placeholder="https://example.com"
                />
            );
        }

        // For checkbox type (boolean)
        if (col_type === 'checkbox' || col_type === 'boolean') {
            return (
                <div className="flex items-center pt-2">
                    <input
                        id={uuid}
                        name={uuid}
                        type="checkbox"
                        checked={formValues[uuid] === '1' || formValues[uuid] === 1}
                        onChange={(e) => handleChange(uuid, e.target.checked ? 1 : 0)}
                        disabled={loading || col_readonly || shouldDisableInEditMode || readOnly}
                        className="h-4 w-4 text-brandColor focus:ring-brandColor border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-600">
                        Check to enable
                    </span>
                    {fieldErrors[uuid] && (
                        <p className="ml-2 text-sm text-red-600">{fieldErrors[uuid]}</p>
                    )}
                </div>
            );
        }

        // For radio buttons (if col_type is radio and has options)
        if (col_type === 'radio' && options && options.length > 0) {
            return (
                <div>
                    <div className="space-y-2">
                        {mapOptions(options).map((option) => (
                            <div key={option.value} className="flex items-center">
                                <input
                                    id={`${uuid}-${option.value}`}
                                    name={uuid}
                                    type="radio"
                                    value={option.value}
                                    checked={formValues[uuid] === option.value}
                                    onChange={(e) => handleChange(uuid, e.target.value)}
                                    disabled={loading || col_readonly || shouldDisableInEditMode || readOnly}
                                    className="h-4 w-4 text-brandColor focus:ring-brandColor border-gray-300"
                                />
                                <label htmlFor={`${uuid}-${option.value}`} className="ml-2 text-sm text-gray-700">
                                    {option.label}
                                </label>
                            </div>
                        ))}
                    </div>
                    {fieldErrors[uuid] && (
                        <p className="mt-1 text-sm text-red-600">{fieldErrors[uuid]}</p>
                    )}
                </div>
            );
        }

        // Default to text input
        return (
            <InputField
                {...commonProps}
                type="text"
            />
        );
    };

    const renderRemarksField = () => {
        if (mode !== 'update') return null;

        return (
            <div className="w-full" ref={remarksFieldRef}>
                <textarea
                    id="remarks"
                    name="remarks"
                    required={true}
                    disabled={loading}
                    placeholder="Enter remarks for this update (required)"
                    value={remarks}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                        handleRemarksChange(e.target.value);
                    }}
                    className={`w-full px-3 py-2 border-2 rounded-md focus:outline-none focus:ring-2 focus:ring-brandColor transition-all duration-300 ${
                        remarksError
                            ? 'border-red-500 bg-red-50 animate-pulse'
                            : 'border-gray-300'
                    }`}
                    rows={3}
                />
                {remarksError && (
                    <div className="mt-2 flex items-center gap-2 p-2 bg-red-100 border border-red-400 rounded-md animate-fade-in">
                        <svg className="h-5 w-5 text-red-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        <p className="text-sm font-medium text-red-800">{remarksError}</p>
                    </div>
                )}
            </div>
        );
    };

    if (loading && columns.length === 0) {
        return (
            <div className="flex items-center justify-center">
                <Loader size={42} className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2" text="Please wait...." />
            </div>
        )
    }

    return (
        <div className="h-full flex flex-col">
            {loading && columns.length === 0 ? (
                <div className="flex items-center justify-center">
                    <Loader size={42} className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2" text="Please wait...." />
                </div>
            ) : (
                <>
                    {/* Form Fields - Scrollable */}
                    <div className="flex-1 overflow-y-auto" ref={formContainerRef}>
                        <form onSubmit={handleSubmit}>
                            {readOnly && readOnlyNote && (
                                <div className="mb-4 bg-gray-50 p-3 rounded-lg border border-gray-200 text-sm text-gray-700">
                                    {readOnlyNote}
                                </div>
                            )}

                            {/* Status Change Mandatory Notice - For is_client=1 schools */}
                            {mode === 'update' && isClientSchool && !readOnly && (
                                <div className="mb-4 bg-orange-50 p-3 rounded-lg border border-orange-300">
                                    <div className="flex items-center gap-2">
                                        <svg className="h-5 w-5 text-orange-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                        </svg>
                                        <span className="text-sm font-medium text-orange-800">
                                            Status change is mandatory when updating leads for this school.
                                        </span>
                                    </div>
                                </div>
                            )}

                            {/* Owner Information Section - Only for Update Mode (hidden in admin view) */}
                            {mode === 'update' && !hideAssignedTo && (
                                <div className="mb-4 bg-brandColor/5 p-3 rounded-lg border border-brandColor/20">
                                    <div className="flex items-center space-x-3">
                                        <label className="text-sm font-medium text-gray-700 w-20">
                                            Assigned To:
                                        </label>
                                        <select
                                            value={selectedOwner}
                                            onChange={(e) => setSelectedOwner(e.target.value)}
                                            className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brandColor"
                                            disabled={loading || readOnly}
                                        >
                                            <option value="">Unassigned</option>
                                            {activeUsers.map((user) => (
                                                <option key={user.value} value={user.value}>
                                                    {user.label} {user.role && `(${user.role})`}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            )}

                            {/* Lead Form Fields Section */}
                            <div className="mb-6">
                                <table className="w-full border-collapse">
                                    <tbody>
                                        {columns
                                            .sort((a, b) => a.col_order - b.col_order)
                                            .map((column, index) => (
                                                <tr key={column.uuid} className={`${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-gray-100 transition-colors`}>
                                                    {/* Label Column */}
                                                    <td className="py-3 px-4 text-sm font-medium text-gray-700 w-1/3 align-top border-b border-gray-200">
                                                        {labelOverrides?.[column.col_name] || column.col_label}
                                                        {column.col_required && !readOnly && <span className="text-red-500 ml-1">*</span>}
                                                    </td>
                                                    {/* Field Column */}
                                                    <td className="py-2 px-4 w-2/3 border-b border-gray-200">
                                                        {renderField(column)}
                                                    </td>
                                                </tr>
                                            ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* Remarks Section - Only for Update Mode */}
                            {mode === 'update' && !readOnly && (
                                <div className="mb-6">
                                    <h3 className="text-lg font-semibold text-gray-800 mb-4 pb-2 border-b">
                                        Update Remarks
                                    </h3>
                                    <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                                        {renderRemarksField()}
                                        <p className="text-sm text-gray-600 mt-2">
                                            <strong>Note:</strong> Adding remarks is required for lead updates to track changes.
                                        </p>
                                    </div>
                                </div>
                            )}
                        </form>
                    </div>

                    {/* Sticky Button at Bottom */}
                    {!readOnly && <div className="border-t bg-white p-4 sticky bottom-0">
                        <div className="flex justify-end space-x-3">
                            <Button
                                onClick={handleSubmit}
                                className="px-6 py-2 bg-brandColor text-white"
                                disabled={loading}
                            >
                                {loading ? 'Submitting...' : mode === 'create' ? 'Create Lead' : 'Update Lead'}
                            </Button>
                        </div>
                    </div>}
                </>
            )}
        </div>
    );
};

export default DynamicLeadForm;